import { useMemo, ReactNode } from "react";
import { useTranslation } from "@coworker/locales";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import CrossIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/CrossIconPath";
import TaskState from "@coworker/apprestructured/src/task-management/types/TaskState";
import ViewerTop from "@coworker/apprestructured/src/task-management/components/TaskViewerSheet/ViewerTop/ViewerTop";
import Texts from "@coworker/apprestructured/src/task-management/components/SheetTop/Texts/Texts";
import Subtitle from "@coworker/apprestructured/src/task-management/components/SheetTop/Subtitle/Subtitle";
import Title from "@coworker/apprestructured/src/task-management/components/SheetTop/Title/Title";
import CloseButtonWrapper from "@coworker/apprestructured/src/task-management/components/SheetTop/CloseButtonWrapper/CloseButtonWrapper";
import Badge from "@coworker/apprestructured/src/task-management/components/SheetTop/Badge/Badge";
import {
  calcDaysOverdue,
  calcOverdueString,
  formatDateTime,
} from "@coworker/apprestructured/src/task-management/util";
import { formatDateByLocale } from "@coworker/reusable/formatters/date.formatter";
import {
  getByPath,
  safeGetByPath,
} from "@coworker/apprestructured/src/shared/helpers/object.helper";
import {
  isLoading,
  Loading,
  LOADING,
} from "@coworker/apprestructured/src/task-management/loading";
import Priority from "../../../SheetTop/Priority/Priority";
import SafetyAlarm from "../../../SheetTop/SafetyAlarm/SafetyAlarm";
import { useCreateTitle } from "@coworker/apprestructured/src/task-management/services/useCreateTitle";
import { getTypedValue } from "@coworker/apprestructured/src/shared/helpers/schemaValidator";
import styles from "./TaskViewerHeader.module.css";

type TaskViewerHeaderProps = {
  task: Record<string, unknown> | Loading;
  onCloseButtonClick: () => void;
  onMenuButtonClick?: () => void;
  topFragment: ReactNode;
  recurrenceText: string | null;
  testId?: string | undefined;
};

function TaskViewerHeader({
  task,
  onCloseButtonClick,
  onMenuButtonClick,
  topFragment,
  recurrenceText,
  testId,
}: TaskViewerHeaderProps) {
  const { t } = useTranslation();
  const title = useCreateTitle(task);
  const created_at = getTypedValue<string>(getByPath(task, "created_at"));
  const completed_at = getTypedValue<string>(getByPath(task, "completed_at"));
  const closed_at = getTypedValue<string>(getByPath(task, "closed_at"));
  const start = getTypedValue<string | null>(getByPath(task, "start"));
  const state = getTypedValue<TaskState>(getByPath(task, "state"));
  const priority = getTypedValue<string>(getByPath(task, "priority"));
  const safetyAlarm = safeGetByPath(task, "details.safety_alarm") === true;

  const subtitle = useMemo(() => {
    if (
      isLoading(completed_at) ||
      isLoading(created_at) ||
      isLoading(closed_at) ||
      isLoading(start)
    ) {
      return LOADING;
    }
    if (state === "completed") {
      return `${t("completd3String")} ${formatDateTime(completed_at)}`;
    } else if (state === "closed") {
      return `${t("closedFirstUppercaseString")} ${formatDateTime(closed_at)}`;
    } else if (start !== null) {
      return `${t("scheduledForString")} ${formatDateByLocale(start)}`;
    } else {
      return `${t("createdString")} ${formatDateTime(created_at)}`;
    }
  }, [closed_at, completed_at, created_at, start, state, t]);

  const badge = useMemo(() => {
    if (
      isLoading(start) ||
      isLoading(state) ||
      start === null ||
      state !== "pending"
    ) {
      return null;
    }

    const daysOverdue = calcDaysOverdue(start);
    if (daysOverdue > 0) {
      return calcOverdueString(daysOverdue, t);
    } else {
      return null;
    }
  }, [start, state, t]);

  return (
    <>
      <ViewerTop state={state} testId={testId || "viewer-header-top"}>
        <CloseButtonWrapper>
          <FixaButton
            data-testid="task-viewer-header-close-button"
            onClick={onCloseButtonClick}
            ssrIcon={CrossIconPath}
            iconOnly
            type="tertiary"
            inverseTheme
            size="small"
          />
        </CloseButtonWrapper>
        <Texts>
          {badge !== null && <Badge text={badge} />}
          <Title light onMenuButtonClick={onMenuButtonClick}>
            {isLoading(title) ? <>&nbsp</> : title}
          </Title>
          {!isLoading(priority) && (priority || safetyAlarm) && (
            <div className={styles["badges"]}>
              {priority && <Priority />}
              {safetyAlarm && <SafetyAlarm />}
            </div>
          )}
          <Subtitle light>
            {isLoading(subtitle) ? <>&nbsp</> : subtitle}
          </Subtitle>
          {recurrenceText && <Subtitle light>{recurrenceText}</Subtitle>}
        </Texts>
      </ViewerTop>
      {topFragment}
    </>
  );
}

export default TaskViewerHeader;
